import {
  CalendarClock,
  Crown,
  Loader2,
  Mail,
  Settings,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { WorkspaceMember, WorkspacePeopleResponse } from "../models/Workspace";
import {
  formatWorkspaceMemberUsage,
  getIncludedTeammateSeats,
  isTeammateSeatSelectionRequired,
} from "../utils/workspaceSeatSelection";

function formatEffectiveDate(value: number) {
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function WorkspacePeoplePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    currentUser,
    activeWorkspace,
    activeWorkspaceId,
    customerData,
    refreshCustomerData,
    refreshWorkspaces,
  } = useAuth();
  const { navigateToUpgrade } = useUpgradePage();
  const [data, setData] = useState<WorkspacePeopleResponse | null>(null);
  const [email, setEmail] = useState("");
  const [workspaceName, setWorkspaceName] = useState(
    activeWorkspace?.name || "",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resendingToken, setResendingToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewingSeatSelection, setReviewingSeatSelection] = useState(false);
  const [seatSelectionIds, setSeatSelectionIds] = useState<string[]>([]);
  const [highlightTeammates, setHighlightTeammates] = useState(false);
  const [sandboxInviteUrl, setSandboxInviteUrl] = useState<string | null>(null);
  const [memberPendingRemoval, setMemberPendingRemoval] =
    useState<WorkspaceMember | null>(null);
  const apiBase = process.env.REACT_APP_API_BASE;

  const request = useCallback(
    async (path: string, init?: RequestInit) => {
      if (!currentUser || !activeWorkspaceId) {
        throw new Error("No active workspace.");
      }
      const token = await currentUser.getIdToken();
      return fetch(`${apiBase}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "x-workspace-id": activeWorkspaceId,
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
          ...init?.headers,
        },
      });
    },
    [currentUser, activeWorkspaceId, apiBase],
  );

  const loadPeople = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const response = await request(
        `/api/workspaces/${activeWorkspaceId}/people`,
      );
      if (!response.ok) {
        throw new Error("Could not load workspace teammates.");
      }
      const people = (await response.json()) as WorkspacePeopleResponse;
      setData(people);
      setSeatSelectionIds(
        people.pendingTeamEntitlement?.selectedMemberIds || [],
      );
      if (
        people.pendingTeamEntitlement &&
        typeof people.pendingTeamEntitlement.confirmedAt !== "number"
      ) {
        setReviewingSeatSelection(true);
      }
      setError(null);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Could not load workspace teammates.",
      );
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, request]);

  useEffect(() => void loadPeople(), [loadPeople]);
  useEffect(() => {
    if (customerData?.pendingTeamEntitlement) void loadPeople();
  }, [customerData?.pendingTeamEntitlement, loadPeople]);
  useEffect(() => {
    setReviewingSeatSelection(false);
    setSeatSelectionIds([]);
  }, [activeWorkspaceId]);
  useEffect(
    () => setWorkspaceName(activeWorkspace?.name || ""),
    [activeWorkspace?.name],
  );
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("focus") !== "teammates") return;

    setHighlightTeammates(true);
    params.delete("focus");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);
  useEffect(() => {
    if (!highlightTeammates) return;
    const timeout = window.setTimeout(
      () => setHighlightTeammates(false),
      2400,
    );
    return () => window.clearTimeout(timeout);
  }, [highlightTeammates]);

  const members = useMemo(
    () =>
      data?.members.slice().sort((a, b) =>
        a.role === "owner"
          ? -1
          : b.role === "owner"
            ? 1
            : a.joinedAt - b.joinedAt,
      ) || [],
    [data?.members],
  );
  const activeCount = useMemo(
    () =>
      members.filter((member) => member.status === "active").length || 0,
    [members],
  );
  const isOwner = data?.currentRole === "owner";
  const pending = data?.pendingTeamEntitlement;
  const pendingSelectionConfirmed =
    typeof pending?.confirmedAt === "number";
  const confirmedMemberIds = useMemo(
    () => new Set(pending?.selectedMemberIds || []),
    [pending?.selectedMemberIds],
  );
  const teammateCount = members.filter(
    (member) => member.role === "member",
  ).length;
  const includedTeammates = getIncludedTeammateSeats(teammateCount, pending);
  const requiresSeatSelection = isTeammateSeatSelectionRequired(
    teammateCount,
    pending,
  );
  const selectingSeats = Boolean(
    isOwner &&
      requiresSeatSelection &&
      (!pendingSelectionConfirmed || reviewingSeatSelection),
  );
  const selectedSeatIds = useMemo(
    () => new Set(seatSelectionIds),
    [seatSelectionIds],
  );
  const inviteEntitlement = data?.entitlement;
  const canInvite =
    inviteEntitlement?.teamEnabled === true &&
    (inviteEntitlement.maxMembers === null ||
      activeCount + (data?.invites.length || 0) < inviteEntitlement.maxMembers);
  const releaseSeatAction = data?.invites.length
    ? "Cancel a pending invitation"
    : activeCount > 1
      ? "Remove a teammate"
      : null;
  const effectiveDate = pending
    ? formatEffectiveDate(pending.effectiveAt)
    : null;
  const memberPendingRemovalLabel =
    memberPendingRemoval?.name ||
    memberPendingRemoval?.email ||
    "this teammate";

  const toggleSeat = (userId: string) => {
    setSeatSelectionIds((current) => {
      if (current.includes(userId)) {
        return current.filter((selectedId) => selectedId !== userId);
      }
      if (current.length >= includedTeammates) return current;
      return [...current, userId];
    });
  };

  const confirmSeatSelection = async () => {
    if (
      !activeWorkspaceId ||
      !pending ||
      seatSelectionIds.length !== includedTeammates
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await request(
        `/api/workspaces/${activeWorkspaceId}/seats`,
        {
          method: "PUT",
          body: JSON.stringify({ selectedMemberIds: seatSelectionIds }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Could not confirm teammate access.");
      }
      await Promise.all([loadPeople(), refreshCustomerData()]);
      setReviewingSeatSelection(false);
    } catch (selectionError) {
      setError(
        selectionError instanceof Error
          ? selectionError.message
          : "Could not confirm teammate access.",
      );
    } finally {
      setSaving(false);
    }
  };

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !activeWorkspaceId) return;
    setSaving(true);
    setError(null);
    try {
      const response = await request(
        `/api/workspaces/${activeWorkspaceId}/invites`,
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Could not send invitation.");
      }
      setSandboxInviteUrl(body.delivery === "captured" ? body.inviteUrl : null);
      setEmail("");
      await loadPeople();
    } catch (inviteError) {
      setError(
        inviteError instanceof Error
          ? inviteError.message
          : "Could not send invitation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind: "members" | "invites", id: string) => {
    if (!activeWorkspaceId) return;
    setSaving(true);
    setError(null);
    try {
      const response = await request(
        `/api/workspaces/${activeWorkspaceId}/${kind}/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Could not update workspace.");
      }
      await loadPeople();
    } catch (removeError) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "Could not update workspace.",
      );
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (token: string) => {
    if (!activeWorkspaceId) return;
    setSaving(true);
    setResendingToken(token);
    setError(null);
    try {
      const response = await request(
        `/api/workspaces/${activeWorkspaceId}/invites/${encodeURIComponent(token)}/resend`,
        { method: "POST" },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Could not resend invitation.");
      }
      setSandboxInviteUrl(body.delivery === "captured" ? body.inviteUrl : null);
      await loadPeople();
    } catch (resendError) {
      setError(
        resendError instanceof Error
          ? resendError.message
          : "Could not resend invitation.",
      );
    } finally {
      setResendingToken(null);
      setSaving(false);
    }
  };

  const rename = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeWorkspaceId || workspaceName.trim() === activeWorkspace?.name) {
      return;
    }
    setSaving(true);
    try {
      const response = await request(`/api/workspaces/${activeWorkspaceId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: workspaceName }),
      });
      if (!response.ok) throw new Error("Could not rename workspace.");
      await refreshWorkspaces();
    } catch (renameError) {
      setError(
        renameError instanceof Error
          ? renameError.message
          : "Could not rename workspace.",
      );
    } finally {
      setSaving(false);
    }
  };

  const memberStatus = (member: WorkspaceMember) => {
    if (pending && pendingSelectionConfirmed && member.role === "member") {
      return confirmedMemberIds.has(member.userId)
        ? "Keeps access"
        : `Access pauses ${effectiveDate}`;
    }
    if (member.status === "suspended") return "Suspended";
    return member.role === "owner" ? "Owner" : "Member";
  };

  return (
    <div className="mx-auto max-w-3xl px-2 pb-16 pt-5 sm:px-6 sm:pt-9">
      <div className="flex items-start gap-3">
        <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand-ink">
          <Settings className="h-4.5 w-4.5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Workspace settings
          </h1>
          <p className="mt-1 max-w-[62ch] text-sm text-muted-foreground">
            Manage this workspace’s name, teammates, and shared access.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="mt-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error}
        </div>
      )}

      <section className="mt-8 rounded-xl border bg-background p-5">
        <h2 className="text-sm font-semibold">Workspace details</h2>
        {isOwner ? (
          <form
            onSubmit={rename}
            className="mt-3 flex flex-col gap-2 sm:flex-row"
          >
            <label htmlFor="workspace-settings-name" className="sr-only">
              Workspace name
            </label>
            <Input
              id="workspace-settings-name"
              value={workspaceName}
              onChange={(event) => setWorkspaceName(event.target.value)}
              maxLength={60}
            />
            <Button
              type="submit"
              variant="outline"
              className="shrink-0 whitespace-nowrap"
              disabled={
                saving ||
                !workspaceName.trim() ||
                workspaceName.trim() === activeWorkspace?.name
              }
            >
              Save name
            </Button>
          </form>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            {activeWorkspace?.name}
          </p>
        )}
      </section>

      <section
        id="teammate-access"
        className={`mt-6 scroll-mt-6 overflow-hidden rounded-xl border bg-background transition-[border-color,box-shadow] duration-700 ease-out motion-reduce:transition-none ${
          highlightTeammates
            ? "border-[oklch(var(--brand)/0.35)] ring-4 ring-[oklch(var(--brand)/0.09)]"
            : ""
        }`}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold">Teammates</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {data
                ? formatWorkspaceMemberUsage(
                    activeCount,
                    data.entitlement.maxMembers,
                  )
                : "Loading member access…"}
            </p>
          </div>
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        </div>

        {pending && effectiveDate && (
          <div
            className={`flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center ${
              selectingSeats ? "bg-brand/5" : "bg-muted/45"
            }`}
          >
            <CalendarClock
              className={`h-4 w-4 shrink-0 ${
                selectingSeats ? "text-brand-ink" : "text-muted-foreground"
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {selectingSeats
                  ? "Choose who keeps access"
                  : `Team access changes on ${effectiveDate}`}
              </p>
              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                {selectingSeats
                  ? `Starting ${effectiveDate}, your plan includes you and ${includedTeammates} teammate${includedTeammates === 1 ? "" : "s"}. We selected the teammates who joined first.`
                  : "Everyone keeps access until then. Memberships and workspace data stay preserved."}
              </p>
            </div>
            {isOwner &&
              requiresSeatSelection &&
              pendingSelectionConfirmed &&
              !selectingSeats && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => {
                  setSeatSelectionIds(pending?.selectedMemberIds || []);
                  setReviewingSeatSelection(true);
                }}
              >
                Change selection
              </Button>
              )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading teammates…
          </div>
        ) : (
          <div className="divide-y">
            {members.map((member) => {
              const isWorkspaceOwner = member.role === "owner";
              const seatChecked =
                isWorkspaceOwner || selectedSeatIds.has(member.userId);
              const selectionFull =
                seatSelectionIds.length >= includedTeammates;
              return (
                <div
                  key={member.userId}
                  className={`flex items-start gap-3 px-5 py-3.5 sm:items-center ${
                    selectingSeats && seatChecked ? "bg-brand/[0.025]" : ""
                  }`}
                >
                  {selectingSeats && (
                    <input
                      type="checkbox"
                      className="mt-2.5 h-4 w-4 shrink-0 rounded border-input accent-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:mt-0"
                      aria-label={`${member.name || member.email} ${
                        isWorkspaceOwner ? "always active" : "keeps access"
                      }`}
                      checked={seatChecked}
                      disabled={
                        isWorkspaceOwner || (!seatChecked && selectionFull)
                      }
                      onChange={() => toggleSeat(member.userId)}
                    />
                  )}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium">
                    {(member.name || member.email || "U")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {member.name || member.email}
                        </span>
                        {member.role === "owner" && (
                          <Crown className="h-3.5 w-3.5 shrink-0 text-brand-ink" />
                        )}
                      </div>
                      {member.name && (
                        <p className="truncate text-xs text-muted-foreground">
                          {member.email}
                        </p>
                      )}
                    </div>
                    {selectingSeats ? (
                      <span className="mt-2 shrink-0 text-xs font-medium text-muted-foreground sm:mt-0">
                        {isWorkspaceOwner
                          ? "Always active"
                          : seatChecked
                            ? "Keeps access"
                            : `Pauses ${effectiveDate}`}
                      </span>
                    ) : (
                      <div className="mt-2 flex items-center justify-between gap-2 sm:mt-0 sm:shrink-0 sm:justify-end">
                        <Badge
                          variant={
                            member.status === "active" ? "secondary" : "outline"
                          }
                          className="whitespace-nowrap font-medium"
                        >
                          {memberStatus(member)}
                        </Badge>
                        {isOwner && member.role !== "owner" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={() => setMemberPendingRemoval(member)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {selectingSeats && (
              <div className="flex flex-col gap-3 bg-muted/20 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {seatSelectionIds.length} of {includedTeammates} teammate seats
                  selected
                </p>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  {pendingSelectionConfirmed && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={saving}
                      onClick={() => {
                        setSeatSelectionIds(pending?.selectedMemberIds || []);
                        setReviewingSeatSelection(false);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      saving ||
                      seatSelectionIds.length !== includedTeammates
                    }
                    onClick={() => void confirmSeatSelection()}
                  >
                    {saving ? "Confirming…" : "Confirm teammate access"}
                  </Button>
                </div>
              </div>
            )}
            {data?.invites.map((pendingInvite) => (
              <div
                key={pendingInvite.token}
                className="flex items-start gap-3 px-5 py-3.5 sm:items-center"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {pendingInvite.email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Invitation expires{" "}
                      {new Date(pendingInvite.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 sm:mt-0 sm:shrink-0 sm:justify-end">
                    <Badge variant="outline">Pending</Badge>
                    {isOwner && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={saving}
                          onClick={() => void resendInvite(pendingInvite.token)}
                        >
                          {resendingToken === pendingInvite.token
                            ? "Resending…"
                            : "Resend"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={saving}
                          onClick={() =>
                            void remove("invites", pendingInvite.token)
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {isOwner && (
        <section className="mt-6 rounded-xl border bg-background p-5">
          <div className="flex items-start gap-3">
            <UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <h2 className="text-sm font-semibold">Invite teammates</h2>
              {inviteEntitlement?.teamEnabled && (
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  They will share this workspace’s brand kits, connections,
                  content, and credits.
                </p>
              )}
            </div>
          </div>
          {inviteEntitlement?.teamEnabled ? (
            <>
              <form
                onSubmit={invite}
                className="mt-4 flex flex-col gap-2 sm:flex-row"
              >
                <label htmlFor="workspace-invite-email" className="sr-only">
                  Email address
                </label>
                <Input
                  id="workspace-invite-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="teammate@company.com"
                  required
                  disabled={!canInvite}
                />
                <Button
                  type="submit"
                  className="shrink-0 whitespace-nowrap"
                  disabled={saving || !email.trim() || !canInvite}
                >
                  {saving ? "Sending…" : "Send invite"}
                </Button>
              </form>
              {!canInvite && (
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  All seats on your current plan are assigned.{" "}
                  {releaseSeatAction ? `${releaseSeatAction}, or ` : null}
                  <button
                    type="button"
                    className="font-medium text-brand-ink underline underline-offset-2 transition-colors hover:text-brand-ink/80"
                    onClick={() =>
                      void navigateToUpgrade({
                        reason: "workspace_seat_limit",
                        customMessage:
                          "Your workspace has reached its teammate limit. Upgrade for more seats.",
                      })
                    }
                  >
                    upgrade for more seats
                  </button>
                  .
                </p>
              )}
              {sandboxInviteUrl && (
                <div className="mt-3 rounded-lg border border-brand/20 bg-brand/5 px-3 py-2.5 text-xs text-muted-foreground">
                  Sandbox email captured.{" "}
                  <a
                    className="font-medium text-brand-ink underline underline-offset-2"
                    href={sandboxInviteUrl}
                  >
                    Open invitation
                  </a>
                </div>
              )}
            </>
          ) : (
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              Your workspace is a single-user workspace.{" "}
              <button
                type="button"
                className="font-medium text-brand-ink underline underline-offset-2 transition-colors hover:text-brand-ink/80"
                onClick={() =>
                  void navigateToUpgrade({
                    reason: "workspace_team_invites",
                    customMessage:
                      "Upgrade to Growth or higher to invite teammates to your workspace.",
                  })
                }
              >
                Upgrade to invite teammates to collaborate.
              </button>
            </p>
          )}
        </section>
      )}

      <AlertDialog
        open={Boolean(memberPendingRemoval)}
        onOpenChange={(open) => {
          if (!open) setMemberPendingRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="break-words">
              Remove {memberPendingRemovalLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They’ll lose access to this workspace immediately. Their work will
              stay in the workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={saving}
              onClick={() => {
                if (!memberPendingRemoval) return;
                void remove("members", memberPendingRemoval.userId);
                setMemberPendingRemoval(null);
              }}
            >
              Remove teammate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
